import { useState } from 'react';
import {
  ROLES,
  getRoleDisplayName,
  getRoleDescription,
  getAssignableRoles,
  canChangeUserRole,
} from '../../../../utils/Auth/roleUtils';
import styles from './RoleSelector.module.css';

/**
 * RoleSelector component - Dropdown for selecting user roles
 * Automatically filters available roles based on current user's permissions
 * 
 * @param {Object} currentUserOrgData - Current user's organization data
 * @param {Object} targetUserOrgData - Target user's organization data (optional for unrestricted mode)
 * @param {Function} onRoleChange - Callback when role is changed: (newRole) => void
 * @param {boolean} disabled - Whether the selector is disabled
 * @param {boolean} unrestricted - If true, shows all assignable roles without permission filtering
 * @param {string} value - Controlled value for the selector (used in unrestricted mode)
 */
const RoleSelector = ({ 
  currentUserOrgData, 
  targetUserOrgData, 
  onRoleChange, 
  disabled = false, 
  unrestricted = false,
  value 
}) => {
  const [isChanging, setIsChanging] = useState(false);

  // In unrestricted mode, show all roles except Primary
  const unrestrictedRoles = [ROLES.VIEWER, ROLES.EDITOR, ROLES.MANAGER, ROLES.ADMIN];
  
  // Determine which roles the current user can assign
  const assignableRoles = unrestricted ? unrestrictedRoles : getAssignableRoles(currentUserOrgData);

  // Check if target is Primary (cannot change role)
  const isPrimaryUser = targetUserOrgData?.is_primary;
  const currentRole = unrestricted ? (value || ROLES.EDITOR) : (targetUserOrgData?.role || ROLES.EDITOR);

  // Check if current user can change this target user's role at all
  const canModifyRole = unrestricted ? true : canChangeUserRole(currentUserOrgData, targetUserOrgData);

  const handleChange = async (e) => {
    const newRole = e.target.value;

    if (newRole === currentRole || !onRoleChange) {
      return;
    }

    setIsChanging(true);
    try {
      await onRoleChange(newRole);
    } finally {
      setIsChanging(false);
    }
  };

  // If user is Primary, show locked display (skip in unrestricted mode)
  if (isPrimaryUser && !unrestricted) {
    return (
      <div className={styles.roleSelectorWrapper}>
        <div className={styles.primaryBadge}>
          <i className="fas fa-crown"></i> Primary
        </div>
      </div>
    );
  }

  // If user is Admin and current user can't modify them, show locked display (skip in unrestricted mode)
  if (currentRole === ROLES.ADMIN && !canModifyRole && !unrestricted) {
    return (
      <div className={styles.roleSelectorWrapper}>
        <div className={styles.lockedBadge} title="Only Primary account holder can modify Admin roles">
          <i className="fas fa-lock"></i> Admin
        </div>
      </div>
    );
  }

  // If user has no assignable roles, show read-only display
  if (assignableRoles.length === 0) {
    return (
      <div className={styles.roleSelectorWrapper}>
        <span className={styles.roleDisplay}>
          {getRoleDisplayName(currentRole)}
        </span>
      </div>
    );
  }

  return (
    <div className={styles.roleSelectorWrapper}>
      <select
        value={currentRole}
        onChange={handleChange}
        disabled={disabled || isChanging}
        className={styles.roleSelect}
        title={getRoleDescription(currentRole)}
      >
        {/* Always show current role even if not in assignable list */}
        {!assignableRoles.includes(currentRole) && (
          <option value={currentRole}>
            {getRoleDisplayName(currentRole)}
          </option>
        )}

        {/* Show all assignable roles */}
        {assignableRoles.map(roleId => (
          <option key={roleId} value={roleId}>
            {getRoleDisplayName(roleId)}
          </option>
        ))}
      </select>

      {isChanging && (
        <span className={styles.spinner}>
          <i className="fas fa-spinner fa-spin"></i>
        </span>
      )}
    </div>
  );
};

export default RoleSelector;
